import {
  MESSAGE_TYPE,
  CONVERSATION_PRIORITY_ORDER,
} from 'shared/constants/messages';
import { toEpochSeconds } from 'dashboard/mixins/conversations';
import { applyPageFilters } from './helpers';
import {
  CONVERSATION_LIST_KEYS,
  findConversationByDisplayId,
  getSelectedChatConversation,
} from './helpers/conversationLists';

const sortComparator = {
  latest: (a, b) => b.last_activity_at - a.last_activity_at,
  sort_on_created_at: (a, b) => a.created_at - b.created_at,
  sort_on_priority: (a, b) => {
    return (
      CONVERSATION_PRIORITY_ORDER[a.priority] -
      CONVERSATION_PRIORITY_ORDER[b.priority]
    );
  },
  sort_on_waiting_since: (a, b) => {
    if (!a.waiting_since && !b.waiting_since) {
      return a.created_at - b.created_at;
    }

    if (!a.waiting_since) {
      return 1;
    }

    if (!b.waiting_since) {
      return -1;
    }

    return a.waiting_since - b.waiting_since;
  },
};

function allLoadedConversationsUnique(state) {
  const seen = new Set();
  const out = [];
  CONVERSATION_LIST_KEYS.forEach(key => {
    (state.conversationLists[key] || []).forEach(c => {
      const n = Number(c.id);
      if (!seen.has(n)) {
        seen.add(n);
        out.push(c);
      }
    });
  });
  return out;
}

// getters
const getters = {
  getAllConversations: state => {
    const { chatSortFilter } = state;
    return allLoadedConversationsUnique(state).sort(
      sortComparator[chatSortFilter]
    );
  },
  getSelectedChat: state => {
    const { selectedChatId } = state;
    const selectedChat = findConversationByDisplayId(state, selectedChatId);
    return selectedChat || {};
  },
  getSelectedChatAttachments: (_state, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    return selectedChat.attachments || [];
  },
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const {
        content_attributes: contentAttributes = {},
        message_type: messageType,
      } = message;
      const { email = {} } = contentAttributes;
      const isIncomingOrOutgoing =
        messageType === MESSAGE_TYPE.OUTGOING ||
        messageType === MESSAGE_TYPE.INCOMING;
      if (email.from && isIncomingOrOutgoing) {
        return true;
      }
      return false;
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;
    return _state.conversationLists.me.filter(conversation => {
      const { assignee } = conversation.meta;
      const isAssignedToMe = assignee && assignee.id === currentUserID;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      return isAssignedToMe && shouldFilter;
    });
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getUnAssignedChats: _state => activeFilters => {
    return _state.conversationLists.unassigned.filter(conversation => {
      const isUnAssigned = !conversation.meta.assignee;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      return isUnAssigned && shouldFilter;
    });
  },
  getAllStatusChats: _state => activeFilters => {
    return _state.conversationLists.all.filter(conversation => {
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      return shouldFilter;
    });
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    const msgs = chat.messages || [];
    const seenSec = toEpochSeconds(chat.agent_last_seen_at);
    return msgs.filter(
      chatMessage =>
        toEpochSeconds(chatMessage.created_at) > seenSec &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getContactSidebarUiFlags: ({ contactSidebarUiFlags }) => contactSidebarUiFlags,
  getConversationsForContact: state => contactId => {
    if (
      contactId === undefined ||
      contactId === null ||
      contactId === '' ||
      Number.isNaN(Number(contactId))
    ) {
      return [];
    }
    const n = Number(contactId);
    return allLoadedConversationsUnique(state)
      .filter(
        c =>
          Number(c.meta?.sender?.id) === n || Number(c.contact_id) === n
      )
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  },
  getConversationById: _state => conversationId => {
    return findConversationByDisplayId(_state, conversationId);
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },
};

export { getSelectedChatConversation };
export default getters;
